import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AccessReportDialogComponent } from './access-report-dialog/access-report-dialog.component';
import { KeyDetectionReportDialogComponent } from './key-detection-report-dialog/key-detection-report-dialog.component';
import { CodeGenReportDialogComponent } from './code-gen-report-dialog/code-gen-report-dialog.component';
import { UsageReportDialogComponent } from './usage-report-dialog/usage-report-dialog.component';
import { InventoryReportDialogComponent } from './inventory-report-dialog/inventory-report-dialog.component';
import { PropertyReportDialogComponent } from './property-report-dialog/property-report-dialog.component';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { AccessReportExportStatus } from 'src/app/models/model';
import { AlertReportDialogComponent } from './alert-report-dialog/alert-report-dialog.component';
import { AppNavigationHeaderComponent } from '../../components/navigation-header/app-navigation-header.component';
import { BreadcrumbLabelDirective } from '../../directives/breadcrumb-label.directive';
import { CardsComponent } from '../../components/cards/cards.component';
import { MatButton } from '@angular/material/button';
@Component({
	selector: 'app-reports',
	templateUrl: './reports.component.html',
	styleUrls: ['./reports.component.scss', '../common/main.scss'],
	imports: [
		AppNavigationHeaderComponent,
		BreadcrumbLabelDirective,
		CardsComponent,
		MatButton,
	],
})
export class ReportsComponent implements OnInit, OnDestroy {
	currentCompanyId: string | undefined;
	activeCompanyId: string | undefined;

	isSentrilock = false;
	isKeyDetectionCompanySettingEnabled = false;
	// hasProperty = false;

	/** The signed-in user's Access Report export for the active company. */
	accessReportExport?: AccessReportExportStatus;

	private exportStatusSubscription?: Subscription;

	breadcrumbLabel = $localize`:@@reportMainTab-breadcrumbLabel:Reports`;

	constructor(
		private readonly dialog: MatDialog,
		private readonly authService: AuthService,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly scService: SystemCapabilityService
	) {
		// RETURNS isSentrilock WHICH IS USED TO DETERMINE IF THE USER IS A SENTRILOCK USER
		this.authService.userSession().subscribe((session) => {
			this.apiService.getSentrilockCompanyId().then((sentrilockId) => {
				this.activeCompanyId =
					session.assignedRole?.entityRelationship?.parentEntityId;
				this.isSentrilock = this.activeCompanyId === sentrilockId;
			});
		});

		// RETURNS true IF PROPERTIES EXIST
		// this.apiService.getPropertyReportFilterOptions().then(options => {
		//   if(options.propertyNames.length >0 ){
		//     this.hasProperty = true
		//   }
		// });
	}

	async ngOnInit(): Promise<void> {
		// checks that the active company has the key detection feature enabled
		this.apiService
			.getCompanySettingByName('keyDetection')
			.then((companySetting) => {
				if (companySetting?.value.toLowerCase().startsWith('true')) {
					this.isKeyDetectionCompanySettingEnabled = true;
				} else {
					this.isKeyDetectionCompanySettingEnabled = false;
				}
			});

		// Run Reports is empty until the session finishes loading, including on refresh.
		this.loadAccessReportExport();
		this.exportStatusSubscription =
			this.scService.systemCapabilitiesUpdated.subscribe(() =>
				this.loadAccessReportExport()
			);
	}

	ngOnDestroy(): void {
		this.exportStatusSubscription?.unsubscribe();
	}

	/** Without Run Reports the status request is a 401, which sends the user to login. */
	private loadAccessReportExport(): void {
		if (!this.scService.has(SystemCapabilityEnum.runReports)) {
			return;
		}
		this.apiService
			.getAccessReportExport()
			.then((status) => {
				this.accessReportExport = status;
			})
			.catch((error) =>
				console.error(
					'Something went wrong while loading the export status:',
					error?.message
				)
			);
	}

	get hasAccessReportExport(): boolean {
		return (
			!!this.accessReportExport && this.accessReportExport.status !== 'none'
		);
	}

	/** Opens Access Report with the filters the export was run with, so the grid matches the CSV. */
	onAccessReportExportClicked(): void {
		const compiledFilters: QueryParam[] = Object.entries(
			this.accessReportExport?.filters ?? {}
		).map(([param, value]) => ({ param, value }));
		this.router.navigateByUrl('/reports/accessReport', {
			state: { compiledFilters, hasFilters: compiledFilters.length > 0 },
		});
	}

	onAccessReportClicked(): void {
		this.dialog
			.open(AccessReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: { queryParams: [] },
			})
			.afterClosed()
			.subscribe((_: any) => {});
	}

	onKeyDetectionReportClicked(): void {
		this.dialog
			.open(KeyDetectionReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: { queryParams: [] },
			})
			.afterClosed()
			.subscribe((_: any) => {});
	}

	onCodeGenReportClicked(): void {
		this.dialog
			.open(CodeGenReportDialogComponent, { panelClass: 'drawer-dialog' })
			.afterClosed()
			.subscribe((_: any) => {});
	}

	onUsageReportClicked(): void {
		this.dialog
			.open(UsageReportDialogComponent, { panelClass: 'drawer-dialog' })
			.afterClosed()
			.subscribe((_: any) => {});
	}

	onInventoryReportClicked(): void {
		this.dialog
			.open(InventoryReportDialogComponent, { panelClass: 'drawer-dialog' })
			.afterClosed()
			.subscribe((_: any) => {});
	}

	onPropertyReportClicked(): void {
		this.dialog
			.open(PropertyReportDialogComponent, { panelClass: 'drawer-dialog' })
			.afterClosed()
			.subscribe((_: any) => {});
	}

	onAlertReportClicked(): void {
		this.dialog
			.open(AlertReportDialogComponent, { panelClass: 'drawer-dialog' })
			.afterClosed()
			.subscribe((_: any) => {});
	}
}
